import { v4 as uuidv4 } from 'uuid';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import React, { useEffect, useState } from 'react';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import { ImageAnnotations } from '@components/shared/ImageAnnotation';
import { ProductListViewed } from '@ops-client/analytics/interfaces';
import Image from '@components/shared/Image/Image';
import breakpoints from '@constants/breakpoints';
import ProductCard from '../ProductCardV2/ProductCard';
import Labels from './Labels';
import styles from './FeaturedBoards.module.scss';
import {
  FeaturedBoards as FeatureBoardsModel,
  FeaturedBoardsReference,
  IFeaturedBoardProduct
} from './model';

interface IFeaturedBoardsProps {
  featuredBoards: FeatureBoardsModel;
  source?: string;
  productsEnabled?: boolean;
  className?: string;
}

const FeaturedBoards = ({
  featuredBoards: {
    boards,
    featuredBoardsReference,
  },
  source = 'shop_home',
  productsEnabled = true,
  className = ''
}: IFeaturedBoardsProps) => {
  const theme = useTheme();

  const isDesktop = useMinBreakpoint('desktop');

  const [analyticsListId, setAnalyticsListId] = useState('');

  if (!boards?.length || !featuredBoardsReference?.length) {
    return null;
  }

  const [boardId, setBoardId] = useState(featuredBoardsReference[0].boardId);

  const selectedBoard = boards.find((item) => {
    return item.id === boardId;
  });

  const boardsVvs = selectedBoard?.boardProducts || [];

  const [boardVvs, setBoardVvs] = useState(boardsVvs);

  useEffect(
    () => {
      const listId = uuidv4();
      const activeTabName = featuredBoardsReference.find((f) => f.boardId === selectedBoard?.id)?.label || '';
      setAnalyticsListId(listId);
      analyticsTrack(
        'product_list_viewed',
        {
          listId,
          listName: `Shop Landing Page | Feature Board | ${activeTabName}`,
          listType: 'standard',
          products: boardVvs,
          pageNumber: 1,
        } as ProductListViewed,
      );
    },
    [boardVvs],
  );

  const handleChangeBoard = (id: string) => {
    setBoardId(id);
    setBoardVvs(selectedBoard?.boardProducts || []);
  };

  const getVvPriorityImage = (id: string, product: IFeaturedBoardProduct) => {
    return featuredBoardsReference.find((item: FeaturedBoardsReference) => item.boardId === id)
      ?.vendorVariants
      .find(
        (p) => p.id === product.id,
      )?.priorityImage;
  };

  return (
    <section className={classNames(
      styles.FeaturedBoards,
      theme.styles.Section,
      className
    )}
    >
      <Labels
        boardId={boardId}
        featuredBoardsReference={featuredBoardsReference}
        onHandleChangeBoard={handleChangeBoard}
      />

      {
        boards.map((board) => {
          const isSelected = selectedBoard?.id === board.id;
          return (
            <div
              key={board.id}
              className={classNames({
                [styles.Content]: isSelected,
                [styles.Inactive]: !isSelected
              })}
            >
              <BoardImageResponsiveWrapper>
                <AspectRatioBox
                  className={styles.BoardImageWrapper}
                  aspectRatio={isDesktop ? (862 / 663) / 2 : (862 / 663)}
                >
                  {board.mainBoardAsset?.asset?.url && (
                  <Image
                    src={board.mainBoardAsset?.asset?.url}
                    placeholder="blur"
                    layout="fill"
                    alt="featured board"
                    sizes={`(min-width: ${breakpoints.desktop}px) 663px, 90vw`}
                    quality={96}
                  />
                  )}

                  {!!board.mainBoardAsset?.asset?.annotations?.length && (
                    <div className={styles.Annotations}>
                      <ImageAnnotations
                        annotations={board.mainBoardAsset?.asset?.annotations}
                        assetId={board.mainBoardAsset?.asset?.id}
                        altInfoBubble
                      />
                    </div>
                  )}

                </AspectRatioBox>
              </BoardImageResponsiveWrapper>

              {!!board.boardProducts.length && (
                <div className={styles.Products}>
                  {board.boardProducts.map((product, index) => {
                    return (
                      <div key={product.id} className={styles.ProductCardWrapper}>
                        <ProductCard
                          product={product}
                          imageIndex={getVvPriorityImage(boardId, product)}
                          imageAspectRatio={361 / 310}
                          listPosition={index}
                          analytics={{
                            location: source || 'shop_home',
                            listId: analyticsListId
                          }}
                          isShopProductCard
                          isClickable={productsEnabled}
                          showSaleBanner
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      }
    </section>
  );
};

/**
 * probably a better way to do this in css but i'm over it at the moment
 */
function BoardImageResponsiveWrapper({ children }: {children: any}) {
  const isDesktop = useMinBreakpoint('desktop');

  if (!isDesktop) {
    return (
      <div className={styles.BoardImageOuterWrapper}>
        {children}
      </div>
    );
  }

  return (
    <>
      {children}
    </>
  );
}

export default FeaturedBoards;
